import { useMemo } from 'react';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import type { Client } from 'src/pages/client-management/types/client.types';
import type { CampaignRequestAction, LaunchedCampaign } from '../types/campaigns.types';
import { formatLaunchedAt } from '../utils/campaigns.utils';
import { CampaignClientSelect } from './campaign-client-select';
import { CampaignRequestActions } from './campaign-request-actions';
import { CampaignStatusBadge } from './campaign-status-badge';
import { SyncStatusIndicator } from './sync-status-indicator';

interface CampaignsTableProps {
  campaigns: LaunchedCampaign[];
  clients: Client[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onAction: (campaignId: string, action: CampaignRequestAction) => Promise<void>;
  onAssignClient: (campaignId: string, clientExtClientId: string) => Promise<void>;
  onRowClick?: (campaignId: string) => void;
  actionsDisabled?: boolean;
}

export function CampaignsTable({
  campaigns,
  clients,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onAction,
  onAssignClient,
  onRowClick,
  actionsDisabled = false,
}: CampaignsTableProps) {
  const columns = useMemo<DataTableColumn<LaunchedCampaign>[]>(() => [
    {
      id: 'name',
      header: 'Campaign name',
      accessor: (campaign) => campaign.name,
      sortable: true,
      render: (campaign) => (
        <span className="font-medium text-[#0D0F1A]">
          {campaign.name}
        </span>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      accessor: (campaign) => campaign.client ?? '',
      sortable: true,
      render: (campaign) => (
        <div onClick={(event) => event.stopPropagation()}>
          <CampaignClientSelect
            campaign={campaign}
            clients={clients}
            disabled={actionsDisabled}
            onAssign={onAssignClient}
          />
        </div>
      ),
    },
    {
      id: 'launchedAt',
      header: 'Launched at',
      accessor: (campaign) => new Date(campaign.launchedAt).getTime(),
      sortable: true,
      render: (campaign) => (
        <span className="font-mono text-[13px] text-[#0D0F1A]">
          {formatLaunchedAt(campaign.launchedAt)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (campaign) => campaign.status,
      render: (campaign) => (
        <CampaignStatusBadge status={campaign.status} />
      ),
    },
    {
      id: 'assignedTo',
      header: 'Assigned to',
      accessor: (campaign) => campaign.assignedToEmail ?? '',
      render: (campaign) => (
        <span className="text-[13px] text-[#0D0F1A]">
          {campaign.assignedToEmail ?? '—'}
        </span>
      ),
    },
    {
      id: 'mondaySync',
      header: 'Monday sync',
      accessor: (campaign) => campaign.mondaySync,
      render: (campaign) => (
        <SyncStatusIndicator
          status={campaign.mondaySync}
          label="Monday sync"
        />
      ),
    },
    {
      id: 'slackSync',
      header: 'Slack notification',
      accessor: (campaign) => campaign.slackSync,
      render: (campaign) => (
        <SyncStatusIndicator
          status={campaign.slackSync}
          label="Slack notification"
        />
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => '',
      render: (campaign) => (
        <CampaignRequestActions
          campaignId={campaign.id}
          status={campaign.status}
          assignedToEmail={campaign.assignedToEmail}
          disabled={actionsDisabled}
          onAction={onAction}
        />
      ),
    },
  ], [actionsDisabled, clients, onAction, onAssignClient]);

  return (
    <DataTable
      rows={campaigns}
      columns={columns}
      pageSize={pageSize}
      getRowKey={(campaign) => campaign.id}
      emptyMessage="No campaigns yet"
      defaultSort={{ columnId: 'launchedAt', direction: 'desc' }}
      serverPagination={{
        page,
        pageSize,
        totalCount,
        onPageChange,
      }}
      onRowClick={onRowClick ? (campaign) => onRowClick(campaign.id) : undefined}
    />
  );
}
